import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import styled from 'styled-components/macro';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';

import {
    Container,
    ButtonGroupWrapper,
    MultipleGroupHalf,
    MultipleGroupFull,
    OnSubmitValidator,
    OptIns as DefaultOptIns,
    CheckboxWrapper,
    RemindersNotice
} from '../../containers/DefaultStyles/FormDetails';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import Button from '../Button';
import InputGroup from '../InputGroup';
import ReactGoogleAutocomplete from '../InputFields/GoogleMapsAddressField';
import TwoGroup from '../TwoGroup';

import { getCachedFirstName, getCachedLastName } from '../../utils/formCaching/helpers';
import submissionErrorHandler from '../../utils/submissionErrorHandler';
import { copyFor, capitalization } from '../../copy/copy';
import validate from './validations';

const OptIns = styled(DefaultOptIns)`
    position: static !important;
    left: unset !important;
    max-width: 100% !important;
    top: unset !important;
`;

class ElectionReminderForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validate)
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
    }

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setValidState(bool) {
        this.setState({ valid: bool });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validate);
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }

    render() {
        return (
            <Container noOverflow>
                <MultipleGroupHalf>
                    <Field
                        name="firstName"
                        onBlur={() => this.checkForBlurError('firstName')}
                        submissionError={this.state.errors.includes('firstName')}
                        clearSubmitErrors={this.clearSubmitErrors}
                        component={InputGroup}
                        type="text"
                        label="First name"
                        placeholder="First Name"
                        autocomplete="off"
                    />
                    <Field
                        name="lastName"
                        onBlur={() => this.checkForBlurError('lastName')}
                        submissionError={this.state.errors.includes('lastName')}
                        clearSubmitErrors={this.clearSubmitErrors}
                        component={InputGroup}
                        type="text"
                        label="Last name"
                        placeholder="Last Name"
                        autocomplete="off"
                    />
                </MultipleGroupHalf>
                <MultipleGroupFull onBlur={() => this.checkForBlurError('address')}>
                    <ReactGoogleAutocomplete
                        submissionError={this.state.errors.includes('address')}
                        clearSubmitErrors={this.clearSubmitErrors}
                        formName={['electionReminderForm', 'google_autocomplete']}
                        fields={['address_components', 'formatted_address']}
                        types={['address']}
                        componentRestrictions={{ country: 'US' }}
                        setValidState={this.setValidState.bind(this)}
                    />
                </MultipleGroupFull>
                <MultipleGroupFull style={{ marginBottom: '12px' }}>
                    <Field
                        name="email"
                        onBlur={() => this.checkForBlurError('email')}
                        submissionError={this.state.errors.includes('email')}
                        clearSubmitErrors={this.clearSubmitErrors}
                        component={InputGroup}
                        type="email"
                        label="Email"
                        placeholder="Email"
                        autocomplete="off"
                    />
                </MultipleGroupFull>
                {(this.props.hasPartnerOptIn || this.props.hasABOptIn) && (
                    <OptIns>
                        <RemindersNotice style={{ fontSize: '10px' }}>
                            {!!this.props.subscriptionOptInText
                                ? this.props.subscriptionOptInText
                                : this.props.endUserLocation.isEU
                                ? copyFor('formFields', 'updatesMultipleEU')
                                : copyFor(
                                      'formFields',
                                      'updatesEmail',
                                      undefined,
                                      capitalization.eachWord
                                  )}
                        </RemindersNotice>
                        <CheckboxWrapper>
                            {this.props.hasPartnerOptIn && (
                                <GetUpdatesCheckbox
                                    style={{ padding: 0 }}
                                    name={'hasPartnerOptIn'}
                                    labelText={this.props.partnerOptInText}
                                    defaultChecked={!this.props.endUserLocation.isEU}
                                    isEU={this.props.endUserLocation.isEU}
                                />
                            )}
                            {this.props.hasABOptIn && (
                                <GetUpdatesCheckbox
                                    style={{ paddingTop: 0, paddingBottom: '5px' }}
                                    isEU={this.props.endUserLocation.isEU}
                                    defaultChecked={!this.props.endUserLocation.isEU}
                                    labelText={'Action Button'}
                                />
                            )}
                        </CheckboxWrapper>
                    </OptIns>
                )}
                <ButtonGroupWrapper
                    isFormSubmitting={this.props.isFormSubmitting}
                    rightPosition={23}
                >
                    <TwoGroup noBack={this.props.skipFrontCard}>
                        {!this.props.skipFrontCard && (
                            <Button label="Back" onClick={this.props.onBack} />
                        )}
                        <OnSubmitValidator
                            onClick={e => {
                                e.preventDefault();
                                const errors = submissionErrorHandler(
                                    this.props.currentFormValues,
                                    validate
                                );
                                if (errors.length === 0 && !this.props.invalid && this.state.valid) {
                                    this.props.handleSubmit();
                                } else {
                                    this.setSubmitErrors(errors);
                                }
                            }}
                        >
                            <Button
                                primary={true}
                                type="submit"
                                label="Continue"
                                className="submitButton"
                                disabled={this.props.invalid || !this.state.valid}
                                isSubmitting={this.props.isFormSubmitting}
                            />
                        </OnSubmitValidator>
                        <LoadingSpinner />
                    </TwoGroup>
                </ButtonGroupWrapper>
            </Container>
        );
    }
}

ElectionReminderForm.propTypes = {
    currentFormValues: PropTypes.object,
    endUserLocation: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func,
    hasABOptIn: PropTypes.bool.isRequired,
    hasPartnerOptIn: PropTypes.bool.isRequired,
    skipFrontCard: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isEU: PropTypes.bool,
    isFormSubmitting: PropTypes.bool,
    onBack: PropTypes.func,
    partnerOptInText: PropTypes.string,
    subscriptionOptInText: PropTypes.string
};

export default compose(
    connect((state, props) => {
        const initialValues = {
            getUpdates: !(state.widget.endUserLocation || {}).isEU && props.hasABOptIn,
            firstName: getCachedFirstName(state),
            lastName: getCachedLastName(state),
            address: state.cachedFormData.address || ''
        };
        if (props.hasPartnerOptIn) {
            initialValues.hasPartnerOptIn = !(state.widget.endUserLocation || {}).isEU;
        }

        return {
            initialValues,
            skipFrontCard: state.widget.activeAction.skipFrontCard,
            currentFormValues: (state.form['electionReminderForm'] || {}).values,
            endUserLocation: state.widget.endUserLocation || {}
        };
    }),
    reduxForm({
        form: 'electionReminderForm',
        destroyOnUnmount: false,
        validate
    })
)(ElectionReminderForm);
